import { readonly, ref } from 'vue';

/**
 * Интерфейс для хранения измерений элемента
 */
export interface ElementMeasurement {
  width: number;
  height: number;
  left: number;
  top: number;
  offsetLeft: number;
  offsetWidth: number;
}

/**
 * Интерфейс для кеша измерений всех элементов меню
 */
export interface MeasurementCache {
  menu: ElementMeasurement | null;
  items: Map<string | number, ElementMeasurement>;
  moreButton: ElementMeasurement | null;
  slider: ElementMeasurement | null;
  version: number;
}

/**
 * Composable для кеширования DOM измерений элементов меню.
 * Минимизирует дорогостоящие вызовы getBoundingClientRect() путём кеширования результатов.
 * 
 * @returns Объект с методами для работы с кешем измерений
 * 
 * @example
 * ```typescript
 * const { cache, measureAll, invalidate, getItemMeasurement } = useElementMeasurements();
 * 
 * // Измерить все элементы за один reflow
 * measureAll(menuEl, itemRefs, moreButtonEl, sliderEl);
 * 
 * // Получить кешированное измерение
 * const itemMeasurement = getItemMeasurement('item-1');
 * 
 * // Инвалидировать кеш при изменениях
 * invalidate();
 * ```
 */
export function useElementMeasurements() {
  const cache = ref<MeasurementCache>({
    menu: null,
    items: new Map(),
    moreButton: null,
    slider: null,
    version: 0
  });

  /**
   * Измеряет один элемент с обработкой ошибок.
   * 
   * @param el - HTML элемент для измерения
   * @returns Измерение элемента или null при ошибке
   */
  const measureElement = (el: HTMLElement | null): ElementMeasurement | null => {
    if (!el) {
      if (!process.env.SERVER) {
        console.warn('[HeaderMenu] Cannot measure null element');
      }
      return null;
    }

    try {
      const rect = el.getBoundingClientRect();
      return {
        width: rect.width,
        height: rect.height,
        left: rect.left,
        top: rect.top,
        offsetLeft: el.offsetLeft,
        offsetWidth: el.offsetWidth
      };
    } catch (error) {
      if (!process.env.SERVER) {
        console.error('[HeaderMenu] Measurement error:', error);
      }
      return null;
    }
  };

  /**
   * Измеряет все элементы меню за один reflow цикл браузера.
   * Все вызовы getBoundingClientRect() выполняются в одном синхронном блоке,
   * что минимизирует количество reflow операций.
   * Включает обработку ошибок для каждого элемента.
   * 
   * @param menuEl - Корневой элемент меню
   * @param itemRefs - Объект с ссылками на элементы меню (ключ - id элемента)
   * @param moreButtonEl - Элемент кнопки "Ещё" (для dropdown режима)
   * @param sliderEl - Элемент слайдера (для slider режима)
   */
  const measureAll = (
    menuEl: HTMLElement | null,
    itemRefs: Record<string, HTMLElement>,
    moreButtonEl: HTMLElement | null,
    sliderEl: HTMLElement | null
  ): void => {
    try {
      // Измерение корневого элемента меню
      if (menuEl) {
        const measurement = measureElement(menuEl);
        if (measurement) {
          cache.value.menu = measurement;
        }
      }

      // Очистка и измерение всех элементов меню
      cache.value.items.clear();
      for (const [id, el] of Object.entries(itemRefs)) {
        if (el) {
          const measurement = measureElement(el);
          if (measurement) {
            cache.value.items.set(id, measurement);
          }
        }
      }

      // Измерение кнопки "Ещё"
      if (moreButtonEl) {
        const measurement = measureElement(moreButtonEl);
        if (measurement) {
          cache.value.moreButton = measurement;
        }
      }

      // Измерение слайдера
      if (sliderEl) {
        const measurement = measureElement(sliderEl);
        if (measurement) {
          cache.value.slider = measurement;
        }
      }
    } catch (error) {
      if (!process.env.SERVER) {
        console.error('[HeaderMenu] Critical error in measureAll:', error);
      }
      // Invalidate cache on critical error
      invalidate();
    }
  };

  /**
   * Инвалидирует весь кеш измерений.
   * Увеличивает версию кеша и очищает все сохранённые измерения.
   * Используется при изменении размеров окна или изменении списка элементов.
   */
  const invalidate = (): void => {
    cache.value.version++;
    cache.value.menu = null;
    cache.value.items.clear();
    cache.value.moreButton = null;
    cache.value.slider = null;
  };

  /**
   * Получает кешированное измерение элемента меню по его id.
   * 
   * @param id - Идентификатор элемента меню
   * @returns Измерение элемента или null, если элемент не был измерен
   */
  const getItemMeasurement = (id: string | number): ElementMeasurement | null => {
    return cache.value.items.get(id) ?? null;
  };

  return {
    cache: readonly(cache),
    measureAll,
    invalidate,
    getItemMeasurement
  };
}
